import { useCallback, useEffect, useRef, useState } from 'react';

import useFetch from 'hooks/useFetch';
import useOAuth2 from 'hooks/useOAuth2';

import {
    JOHN_DEERE_AUTH_URL,
    JOHN_DEERE_CLIENT_ID,
    JOHN_DEERE_CONNECTION_API,
    JOHN_DEERE_REDIRECT_URI,
    JOHN_DEERE_SCOPES,
    JOHN_DEERE_TOKEN_URL
} from './constants';
import type { UseJohnDeereReturn } from './types';

type ConnectionStatus = {
    enabled: boolean;
};

const FETCH_PARAMS = {
    apiHost: import.meta.env.VITE_JD_API_BASE_URL || '',
    headers: { 'Content-Type': 'application/json' }
};

const useJohnDeere = (): UseJohnDeereReturn => {
    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const hasCheckedConnection = useRef(false);

    const { authenticatedDelete, authenticatedGet, authenticatedPost } = useFetch(FETCH_PARAMS);

    const { codeVerifier, fetchTokenOrRedirectAndLogIn } = useOAuth2({
        authorizationEndpoint: JOHN_DEERE_AUTH_URL,
        clientId: JOHN_DEERE_CLIENT_ID,
        redirectUri: JOHN_DEERE_REDIRECT_URI,
        scope: JOHN_DEERE_SCOPES,
        setToken: () => {},
        token: null,
        tokenEndpoint: JOHN_DEERE_TOKEN_URL
    });

    useEffect(() => {
        if (hasCheckedConnection.current) {
            return;
        }

        hasCheckedConnection.current = true;

        const checkStatus = async () => {
            try {
                const response =
                    await authenticatedGet<ConnectionStatus>(JOHN_DEERE_CONNECTION_API);

                if (response.data) {
                    setIsConnected(response.data.enabled);
                }
            } catch {
                setIsConnected(false);
            }
        };

        checkStatus();
    }, [authenticatedGet]);

    const connect = useCallback(async () => {
        await fetchTokenOrRedirectAndLogIn('/connections');
    }, [fetchTokenOrRedirectAndLogIn]);

    const createConnection = useCallback(
        async ({ code }: { code: string }) => {
            try {
                setIsConnecting(true);

                const response = await authenticatedPost(
                    JOHN_DEERE_CONNECTION_API,
                    JSON.stringify({
                        code,
                        codeVerifier,
                        redirectUri: JOHN_DEERE_REDIRECT_URI
                    })
                );

                if (response.error) {
                    throw new Error(response.error);
                }

                setIsConnected(true);
            } catch (connectionError) {
                const message =
                    connectionError instanceof Error ? connectionError.message : 'Unknown error';

                console.error('John Deere connection failed:', message);
                throw connectionError;
            } finally {
                setIsConnecting(false);
            }
        },
        [authenticatedPost, codeVerifier]
    );

    const disconnect = useCallback(async () => {
        await authenticatedDelete(JOHN_DEERE_CONNECTION_API);
        setIsConnected(false);
        hasCheckedConnection.current = true;
    }, [authenticatedDelete]);

    return {
        connect,
        createConnection,
        disconnect,
        isConnected,
        isConnecting
    };
};

export default useJohnDeere;
